import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Fade,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion, AnimatePresence } from 'framer-motion';

const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  minHeight: '400px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
}));

const QuestionContainer = styled(motion.div)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  position: 'relative',
});

const QuestionWrapper = styled(Box)({
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
});

const BackArrow = styled(IconButton)(({ theme }) => ({
  color: 'white',
  opacity: 0.5,
  padding: '4px',
  marginLeft: '-8px',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.8,
    backgroundColor: 'transparent',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

const StyledInput = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    fontSize: '1.5rem',
    textAlign: 'center',
    caretColor: 'white',
    color: 'white',
    padding: '12px 16px',
  },
  '& .MuiInput-underline:before': {
    borderBottom: 'none',
  },
  '& .MuiInput-underline:after': {
    borderBottom: 'none',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottom: 'none',
  },
  '& .MuiInputBase-root': {
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      border: '1px solid rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused': {
      border: '1px solid rgba(255, 255, 255, 0.5)',
      boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.1)',
    },
  },
  width: '100%',
  maxWidth: '400px',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: '12px 32px',
  borderRadius: '30px',
  fontSize: '1.1rem',
  textTransform: 'none',
  backgroundColor: 'white',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '0.9rem',
  backgroundColor: 'rgba(255, 59, 48, 0.15)',
  padding: '8px 16px',
  borderRadius: '8px',
  width: '100%',
  maxWidth: '400px',
  textAlign: 'center',
  marginTop: '16px',
}));

export interface Question {
  id: string;
  question: string;
  placeholder: string;
  validation: (value: string) => string;
  multiline?: boolean;
  rows?: number;
}

export interface TypeformStyleProps {
  questions: Question[];
  onSubmit: (formData: Record<string, string>) => Promise<void>;
  successMessage?: {
    title?: string;
    description?: string;
  };
  submitButtonText?: string;
  continueButtonText?: string;
  showKeyboardHint?: boolean;
}

export const TypeformStyle: React.FC<TypeformStyleProps> = ({
  questions,
  onSubmit,
  successMessage = {
    title: 'Thank you!',
    description: 'We\'ll get back to you within 2 working days.',
  },
  submitButtonText = 'Submit',
  continueButtonText = 'Continue',
  showKeyboardHint = true,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, string>>(
    questions.reduce((acc, q) => ({ ...acc, [q.id]: '' }), {})
  );
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const focusInput = () => {
    if (currentStep === 0) return;
    
    const input = inputRef.current;
    if (input && 'focus' in input) {
      input.focus();
      if ('selectionStart' in input) {
        const len = input.value.length;
        input.selectionStart = len;
        input.selectionEnd = len;
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setError('');
    }
  };

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && currentStep > 0) {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [currentStep]);

  const handleNext = () => {
    const currentQuestion = questions[currentStep];
    const currentValue = formData[currentQuestion.id];
    const validationError = currentQuestion.validation(currentValue);
    
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError('');
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setIsComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  if (isComplete) {
    return (
      <FormContainer>
        <Fade in>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'white' }} />
            <Typography variant="h4" color="white" gutterBottom>
              {successMessage.title}
            </Typography>
            <Typography color="white" variant="body1">
              {successMessage.description}
            </Typography>
          </Box>
        </Fade>
      </FormContainer>
    );
  }

  const currentQuestion = questions[currentStep];

  return (
    <FormContainer>
      <AnimatePresence mode="wait">
        <QuestionContainer
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          onAnimationComplete={focusInput}
        >
          {currentStep > 0 && (
            <QuestionWrapper>
              <BackArrow
                onClick={handleBack}
                aria-label="Go back to previous question"
              >
                <ArrowBackIcon />
              </BackArrow>
              <Typography variant="h4" color="white" gutterBottom>
                {currentQuestion.question}
              </Typography>
            </QuestionWrapper>
          )}
          {currentStep === 0 && (
            <Typography variant="h4" color="white" gutterBottom>
              {currentQuestion.question}
            </Typography>
          )}
          
          <StyledInput
            inputRef={inputRef}
            variant="standard"
            placeholder={currentQuestion.placeholder}
            value={formData[currentQuestion.id]}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                [currentQuestion.id]: e.target.value
              }));
              setError('');
            }}
            onKeyPress={handleKeyPress}
            multiline={currentQuestion.multiline}
            rows={currentQuestion.rows}
            InputProps={{
              style: { color: 'white' }
            }}
            autoComplete="off"
          />

          {error && (
            <ErrorMessage variant="body2">
              {error}
            </ErrorMessage>
          )}

          <ActionButton
            onClick={handleNext}
            disabled={isSubmitting}
            endIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {currentStep === questions.length - 1 ? submitButtonText : continueButtonText}
          </ActionButton>

          {showKeyboardHint && (
            <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ 
              mt: 2,
              '@media (hover: hover) and (pointer: fine)': {
                '&::after': {
                  content: currentStep > 0 ? '", Esc to go back"' : '""',
                },
              },
            }}>
              Press Enter ↵ to continue
            </Typography>
          )}
        </QuestionContainer>
      </AnimatePresence>
    </FormContainer>
  );
}; 